import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../contexts/AuthContext'
import { useAlerts } from '../../contexts/AlertContext'
import { useWorkspace } from '../../contexts/WorkspaceContext'
import { usePermissionCheck } from '../../contexts/PermissionContext'
import apiService from '../../services/api'
import {
  HiOutlineArrowLeft,
  HiOutlineFolder,
  HiOutlineUser,
  HiOutlineCalendarDays,
  HiOutlineDocumentText,
  HiOutlineCheckCircle,
  HiOutlineUserPlus,
} from 'react-icons/hi2'
import {
  Banner,
  Button,
  Card,
  FormField,
  IconButton,
  Input,
  Modal,
  PageHeader,
  Select,
  Textarea,
} from '../../components/ui'

export default function CreateCasePage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { user, isWorkspaceSwitched, ownerAccountId } = useAuth()
  const { showSuccess, showError } = useAlerts()
  const { getEntity } = useWorkspace()
  const { canCreate } = usePermissionCheck()

  useEffect(() => {
    if (!canCreate('case')) {
      showError(t('common.permissionDenied'))
      navigate(-1)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canCreate])

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    client: searchParams.get('client') || '',
    date: new Date().toISOString().split('T')[0],
  })
  const [clients, setClients] = useState([])
  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)
  const [clientsLoading, setClientsLoading] = useState(true)

  // Quick-create client modal
  const [showCreateClientModal, setShowCreateClientModal] = useState(false)
  const [newClientData, setNewClientData] = useState({ name: '', email: '', mobile: '' })
  const [newClientErrors, setNewClientErrors] = useState({})
  const [creatingClient, setCreatingClient] = useState(false)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const params = isWorkspaceSwitched && ownerAccountId ? { owner_account_id: ownerAccountId } : {}
        // page=all: list endpoints return 10 items per page by default, which left
        // older entries out of this dropdown.
        const response = await apiService.get('/clients/', { params: { ...params, page: 'all' } })
        if (cancelled) return
        if (response.success) {
          const data = response.data.data || response.data
          setClients(data.data || data.results || [])
        }
      } catch { /* swallow */ } finally {
        if (!cancelled) setClientsLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [ownerAccountId, isWorkspaceSwitched])

  const handleNewClientChange = (e) => {
    const { name, value } = e.target
    setNewClientData(prev => ({ ...prev, [name]: value }))
    if (newClientErrors[name]) setNewClientErrors(prev => ({ ...prev, [name]: '' }))
  }

  const handleQuickCreateClient = async () => {
    if (!newClientData.name.trim()) {
      setNewClientErrors({ name: t('cases.create.quickClientNameRequired', { client: getEntity('client').singular }) })
      return
    }
    try {
      setCreatingClient(true)
      const payload = {
        name: newClientData.name.trim(),
        email: newClientData.email.trim() || null,
        mobile: newClientData.mobile.trim() || null,
      }
      const params = isWorkspaceSwitched && ownerAccountId ? { owner_account_id: ownerAccountId } : {}
      const response = await apiService.post('/clients/', payload, { params })
      if (response.success) {
        const newClient = response.data.data || response.data
        setClients(prev => [...prev, newClient])
        setFormData(prev => ({ ...prev, client: newClient.id.toString() }))
        setNewClientData({ name: '', email: '', mobile: '' })
        setShowCreateClientModal(false)
        showSuccess(t('cases.create.quickClientCreated', { client: getEntity('client').singular }))
      } else if (response.error?.data) {
        setNewClientErrors(response.error.data)
      } else {
        showError(response.error?.message || t('clients.deleteFailed', { entity: getEntity('client').singular.toLowerCase() }))
      }
    } catch {
      showError(t('clients.deleteFailed', { entity: getEntity('client').singular.toLowerCase() }))
    } finally {
      setCreatingClient(false)
    }
  }

  const validate = () => {
    const next = {}
    if (!formData.title.trim()) next.title = t('cases.create.validationTitleRequired', { entity: getEntity('case').singular })
    if (!formData.client) next.client = t('cases.create.validationClientRequired', { client: getEntity('client').singular.toLowerCase() })
    if (!formData.date) next.date = t('cases.create.validationDateRequired', { entity: getEntity('case').singular })
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validate()) return
    try {
      setLoading(true)
      const payload = {
        title: formData.title.trim(),
        description: formData.description.trim() || null,
        client_id_input: parseInt(formData.client),
        date: formData.date,
      }
      const params = isWorkspaceSwitched && ownerAccountId ? { owner_account_id: ownerAccountId } : {}
      const response = await apiService.post('/cases/', payload, { params })
      if (response.success) {
        showSuccess(t('cases.create.successMessage', { entity: getEntity('case').singular }))
        navigate('/cases')
      } else if (response.error?.data) {
        setErrors(response.error.data)
      } else {
        showError(response.error?.message || t('cases.deleteFailed', { entity: getEntity('case').singular.toLowerCase() }))
      }
    } catch {
      showError(t('cases.deleteFailed', { entity: getEntity('case').singular.toLowerCase() }))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <IconButton
          icon={HiOutlineArrowLeft}
          aria-label={t('cases.detail.backTo', { entity: getEntity('case').plural })}
          variant="ghost"
          onClick={() => navigate('/cases')}
          className="rtl:rotate-180"
        />
        <PageHeader
          icon={HiOutlineFolder}
          iconTone="info"
          title={t('cases.create.title', { entity: getEntity('case').singular.toLowerCase() })}
          subtitle={
            isWorkspaceSwitched
              ? t('cases.create.subtitleSwitched', { entity: getEntity('case').singular.toLowerCase(), name: user?.switchedAccountName })
              : t('cases.create.subtitle', { entity: getEntity('case').singular.toLowerCase() })
          }
          className="!mb-0 flex-1"
        />
      </div>

      <Card padding="none">
        <Card.Header className="px-5 sm:px-6 py-4 mb-0 border-b border-border-subtle">
          <div className="flex items-center gap-2">
            <HiOutlineFolder className="w-4 h-4 text-ink-muted" />
            <h2 className="text-sm font-semibold text-ink-primary">
              {t('cases.create.sectionTitle', { entity: getEntity('case').singular })}
            </h2>
          </div>
        </Card.Header>

        <form onSubmit={handleSubmit} className="p-5 sm:p-6 space-y-5">
          <FormField label={t('cases.create.titleLabel', { entity: getEntity('case').singular })} required error={errors.title}>
            <Input
              name="title"
              size="lg"
              value={formData.title}
              onChange={handleChange}
              leadingIcon={HiOutlineFolder}
              placeholder={t('cases.create.titlePlaceholder', { entity: getEntity('case').singular.toLowerCase() })}
              disabled={loading}
              autoFocus
            />
          </FormField>

          <FormField
            label={t('cases.create.clientLabel', { client: getEntity('client').singular })}
            required
            error={errors.client}
            help={
              <span className="inline-flex items-center gap-1">
                {t('cases.create.notInList')}
                <button
                  type="button"
                  onClick={() => setShowCreateClientModal(true)}
                  className="text-brand hover:text-brand-hover font-medium inline-flex items-center gap-0.5"
                >
                  <HiOutlineUserPlus className="w-3.5 h-3.5" />
                  {t('cases.create.addNewClient', { client: getEntity('client').singular.toLowerCase() })}
                </button>
              </span>
            }
          >
            <Select
              name="client"
              size="lg"
              value={formData.client}
              onChange={handleChange}
              disabled={loading || clientsLoading}
            >
              <option value="">
                {clientsLoading ? t('common.loading') : t('cases.create.clientPlaceholder', { client: getEntity('client').singular.toLowerCase() })}
              </option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </Select>
          </FormField>

          <FormField label={t('cases.create.dateLabel', { entity: getEntity('case').singular })} required error={errors.date}>
            <Input
              type="date"
              name="date"
              size="lg"
              value={formData.date}
              onChange={handleChange}
              leadingIcon={HiOutlineCalendarDays}
              disabled={loading}
            />
          </FormField>

          <FormField
            label={t('cases.create.descriptionLabel')}
            error={errors.description}
            help={t('cases.create.descriptionHelp')}
          >
            <Textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={4}
              placeholder={t('cases.create.descriptionPlaceholder', { entity: getEntity('case').singular.toLowerCase() })}
              disabled={loading}
            />
          </FormField>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-6 border-t border-border-subtle">
            <Button type="button" variant="secondary" onClick={() => navigate('/cases')} disabled={loading}>
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              size="lg"
              leadingIcon={loading ? null : HiOutlineCheckCircle}
              loading={loading}
            >
              {loading
                ? t('cases.create.submitLoading', { entity: getEntity('case').singular.toLowerCase() })
                : t('cases.create.submit', { entity: getEntity('case').singular.toLowerCase() })}
            </Button>
          </div>
        </form>
      </Card>

      <Banner tone="info" title={t('cases.create.guidelinesTitle', { entity: getEntity('case').singular })}>
        <ul className="text-sm space-y-1 list-disc list-inside ms-1">
          <li>{t('cases.create.guideline1', { client: getEntity('client').singular.toLowerCase() })}</li>
          <li>{t('cases.create.guideline2')}</li>
          <li>{t('cases.create.guideline3', {
            sessions: getEntity('session').plural.toLowerCase(),
            documents: getEntity('document').plural.toLowerCase(),
          })}</li>
        </ul>
      </Banner>

      {/* Quick-create client modal */}
      <Modal
        isOpen={showCreateClientModal}
        onClose={() => setShowCreateClientModal(false)}
        title={t('cases.create.quickClientTitle', { client: getEntity('client').singular.toLowerCase() })}
        size="md"
      >
        <Modal.Body>
          <p className="text-sm text-ink-muted mb-4">
            {t('cases.create.quickClientDescription', { client: getEntity('client').singular.toLowerCase() })}
          </p>
          <div className="space-y-4">
            <FormField label={t('clients.create.nameLabel', { entity: getEntity('client').singular })} required error={newClientErrors.name}>
              <Input
                name="name"
                value={newClientData.name}
                onChange={handleNewClientChange}
                leadingIcon={HiOutlineUser}
                placeholder={t('clients.create.namePlaceholder', { entity: getEntity('client').singular.toLowerCase() })}
                disabled={creatingClient}
                autoFocus
              />
            </FormField>
            <FormField label={t('clients.create.emailLabel')} error={newClientErrors.email}>
              <Input
                type="email"
                name="email"
                value={newClientData.email}
                onChange={handleNewClientChange}
                placeholder={t('common.optional').toLowerCase()}
                disabled={creatingClient}
              />
            </FormField>
            <FormField label={t('clients.create.mobileLabel')} error={newClientErrors.mobile}>
              <Input
                type="tel"
                name="mobile"
                value={newClientData.mobile}
                onChange={handleNewClientChange}
                placeholder={t('common.optional').toLowerCase()}
                disabled={creatingClient}
              />
            </FormField>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="ghost" onClick={() => setShowCreateClientModal(false)} disabled={creatingClient}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleQuickCreateClient} loading={creatingClient}>
            {t('clients.create.submit', { entity: getEntity('client').singular.toLowerCase() })}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}
